/**
 * The API client.
 *
 * Two transports, because the two regimes have incompatible physics
 * (design §3.1):
 *
 *   historical  -> fetch + ETag/304, keyset cursors, hard cap 200
 *   live        -> SSE (Last-Event-ID is the resync semantics), no cache
 *
 * Three laws this module enforces at the wire, not by convention:
 *
 *  1. **A request cannot name a tenant.** `qs()` throws on a `tenant` /
 *     `tenant_id` key. Tenancy derives from the session cookie -> principal
 *     -> tenant_id, server-side, and from nowhere else (ARCHITECTURE §1.1).
 *  2. **A range is bounded by its LIMIT, not by its lower bound.** All-time
 *     exists: it is the ABSENCE of `since` and `until`, not a magic parameter,
 *     and it reads the same tenant-leading index with the same keyset cursor
 *     and the same page cap as a 24-hour range. What the old dashboard got
 *     wrong was not the question — it was shipping an unbounded answer. There
 *     is still no `window` or `bracket` parameter on the wire: the client
 *     resolves the preset and sends explicit instants, because YTD and a
 *     custom range are questions about the reader's DISPLAY timezone, which
 *     the server has never been told.
 *  3. **Every list is bounded.** `limit` is clamped to PAGE_CAP here as well
 *     as on the server, so a hand-built call site cannot ask for everything.
 *
 * The client NEVER computes a displayed number from a partial page and never
 * interpolates a chart value. If the server did not send it, it is not on
 * the screen.
 */

export const PAGE_CAP = 200;
export const PAGE_DEFAULT = 50;

/* ------------------------------------------------------------------ types */

export type Certainty = "MEASURED" | "BOUNDED" | "DERIVED" | "UNMEASURABLE";
export type Clock = "MONO" | "OUR_WALL" | "X_HOST" | "SRV_TO_OUR" | "OUR_TO_VENUE" | "MIXED";
export type Attestation = "BROKER" | "ENGINE" | "ESTIMATED" | "PENDING" | "ABANDONED" | "NONE";

export interface Span {
  from_hop: string;
  to_hop: string;
  ms: number | null;
  clock: Clock;
  certainty: Certainty;
  /** 30 d empirical minimum for this hop; drawn BEHIND the bar, never subtracted. */
  floor_ms: number | null;
  /** clock-domain boundaries crossed. A total is never a bare sum. */
  seams: number;
  domains: string[];
  note: string | null;
}

export interface Bound {
  low: number;
  high: number;
  basis: string;
}

export interface Attested {
  value: number | null;
  unit: "usd" | "pct";
  attestation: Attestation;
  source: string | null;
  bound: Bound | null;
  note: string | null;
}

export interface Denominators {
  included: number;
  excluded_estimated: number;
  excluded_unresolved: number;
  excluded_out_of_scope: number;
  skipped_unparseable: number;
  lower_bound: boolean;
}

export interface PageMeta {
  next_cursor: string | null;
  limit: number;
  returned: number;
  skipped: number;
  scope: string;
  /** Rows dropped because the requested sort key was NULL on them. A P&L sort
   *  cannot place an unresolved close, so those rows leave the page and are
   *  COUNTED out loud rather than vanishing. */
  excluded_null_sort_key?: number;
}

/** The resolved range, as the server understood it. `is_all` is the absence
 *  of both bounds, `label` is whatever the client asked it to echo. */
export interface WindowSpec {
  since: string | null;
  until: string | null;
  label: string;
  length_s: number | null;
  is_all: boolean;
}

/** THE PER-BRACKET AGGREGATE. Every figure below is a single-row SQL
 *  aggregate over the bracket — never a fold over the page the reader
 *  happens to be looking at. Three different clocks sit in one frame and
 *  `clocks` names which is which; that is why `cases` and `money` can report
 *  different populations for the same words on screen. */
export interface SummaryPayload {
  window: WindowSpec;
  clocks: { cases: string; money: string; opens: string; latency: string };
  cases: {
    n: number;
    strikes: number;
    faults: number;
    no_trade: number;
    first_at: string | null;
    last_at: string | null;
    by_source: Array<{ source: string; n: number }>;
    by_outcome: Array<{ outcome_word: string; n: number }>;
    denominators: Denominators;
  };
  money: SummaryMoney;
  latency: {
    hop: string;
    n: number;
    p50_ms: number | null;
    p90_ms: number | null;
    clock: Clock | null;
    certainty: Certainty | null;
    clamp_above_ms: number;
    n_clamped: number;
  };
}

/** Bracketed on **close_ts** — realized P&L belongs to the close, so "cases in
 *  24H" and "realized in 24H" are different populations by construction. */
export interface SummaryMoney {
  realized: Attested;
  return_pct: Attested | null;
  n_closed: number;
  n_attested: number;
  n_estimated: number;
  n_pending: number;
  wins: number;
  losses: number;
  flats: number;
  /** wins / n_attested, 0..1. null when the denominator is empty — a rate over
   *  nothing is not shipped. */
  win_rate: number | null;
  best_pct: number | null;
  worst_pct: number | null;
  median_hold_s: number | null;
  n_open_at_end: number;
  balance: { value: number; src: "daily"; ts: string; read_age_s: number } | null;
  /** Equity INCLUDING unrealized on a full-balance position at 10–20×. A
   *  SEPARATE field for exactly that reason: it is never "the balance". */
  balance_mark: { value: number; src: "log"; ts: string } | null;
  sortino: number | null;
  sharpe: null;
  sharpe_suppressed: "sqrt_n";
  sharpe_suppressed_reason: string;
  max_drawdown_pct: number | null;
  series_capped: boolean;
  denominators: Denominators;
}

export interface Page<T> {
  meta: PageMeta;
  rows: T[];
}

/** The race summary as it appears on a feed row.
 *
 *  Field names are IDENTICAL to the `race` table's columns
 *  (index/schema.sql §2) so nothing has to be renamed when the feed
 *  projection starts carrying them. Today the router's FEED_COLS does not
 *  select them; `RaceGlyph` renders the honest absence in that case rather
 *  than a fabricated bar. Recorded as a gap. */
export interface RaceSummary {
  fast_present?: number | boolean | null;
  fast_p2d_ms?: number | null;
  smart_p2d_ms?: number | null;
  head_start_ms?: number | null;
  winner?: string | null;
  fast_lost_at?: string | null;
  fast_boundary?: number | boolean | null;
  fast_winner_idx?: number | null;
  fast_slots?: Array<{ idx: number; ms: number | null; err?: string | null }> | null;
}

export interface CaseRow {
  post_id: string;
  source: string;
  kind: string | null;
  created_at: string | null;
  created_at_clock?: string | null;
  first_seen_at?: string | null;
  state: string;
  settlement: string;
  revision: number;
  outcome_word: string;
  no_trade_reason: string | null;
  machine_verdict: string | null;
  checks_run: number;
  checks_not_evaluable: number;
  strategy_verdict: string | null;
  /** <=90 chars. A list payload NEVER carries content_text / content_text_en. */
  excerpt: string;
  instrument: string | null;
  direction: string | null;
  conf: number | null;
  signal: string | null;
  via: string | null;
  mode: string | null;
  venue: string | null;
  headline_span_ms: number | null;
  headline_span_hop: string | null;
  headline_clock: Clock | null;
  headline_certainty: Certainty | null;
  /** optional — see RaceSummary */
  race?: RaceSummary | null;
}

export interface OrderRow {
  trade_id: string;
  post_id: string | null;
  pairing: "keyed" | "inferred";
  state: string;
  source: string | null;
  instrument: string | null;
  direction: string | null;
  conf: number | null;
  mode: string | null;
  venue: string | null;
  deal_id: string | null;
  deal_ref: string | null;
  size: number | null;
  entry_price: number | null;
  entry_est: number | null;
  slippage_bps: number | null;
  sl_dist: number | null;
  tp_dist: number | null;
  hold_seconds: number | null;
  order_post_ms: number | null;
  order_post_semantics: string | null;
  venue_ack_ms: number | null;
  confirm_ms: number | null;
  open_ts: string | null;
  close_ts: string | null;
  close_reason: string | null;
  close_evidence: string | null;
  exit_price: number | null;
  pnl_usd: number | null;
  pnl_pct: number | null;
  pnl_source: string | null;
  fill_time: string | null;
  is_open: number;
  unterminated: number;
  pnl: Attested;
  waterfall?: Array<Span | null>;
  totals?: Array<{ label: string; ms: number | null; seams: number; note: string }>;
  ack_label?: string;
}

export interface EquityPoint {
  ts: string;
  balance: number;
  /** measured | measured_unrealized | synth_post_close | synth_pre_trade | flow */
  kind: string;
  /** daily | log | synth | flow — `log` is the MARK population and is NEVER
   *  joined to the realized line. */
  src: string;
  note?: string | null;
}

export interface EquityPayload {
  points: EquityPoint[];
  gaps: Array<{ from: string; to: string; seconds: number }>;
  flows: Array<{ ts: string; amount_usd: number | null; kind: string }>;
  band: { low: number; high: number; n_unsettled: number; basis: string };
  anchor: EquityPoint | null;
  unterminated_spans: Array<{ open: string | null; clamped_to: string | null }>;
  denominators: Denominators;
  mark_overlay: { included: boolean; why: string };
}

export interface StatePayload {
  state: "ARMED" | "WATCHING" | "BLIND" | "UNKNOWN";
  why: string;
  arming: string;
  exposure: OrderRow | null;
  since_you_looked: { n: number; strikes: number | null; faults: number | null } | null;
  surface: {
    read_at?: string;
    tailer_lag_ms?: number | null;
    compactor_lag_ms?: number | null;
    overflow_n?: number;
    parity_age_s?: number | null;
    last_push_at?: string | null;
  } | null;
  conditions: Array<{
    name: string;
    state: string;
    read_age_s: number | null;
    degraded_telemetry: boolean;
    blocks: string | null;
  }>;
  note: string;
}

export interface GateRow {
  name: string;
  enabled: boolean;
  updated_at: string | null;
  updated_by: string | null;
  fails_closed: boolean;
  note: string;
  apply?: { state: string; note?: string; evidence_case?: { post_id: string; created_at: string } };
}

/* ---------------------------------------------------------------- plumbing */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** A parameter name a request may never carry.
 *
 *  These two, and ONLY these two. `window` and `all` were here once, and they
 *  are gone: all-time is a legitimate reading and it travels as the absence
 *  of bounds. Tenancy is untouched by that change and is not up for
 *  negotiation — a client that can name a tenant is a client that can read
 *  another customer's money, so it is refused where the URL is built rather
 *  than in a review comment. */
const FORBIDDEN_PARAMS = new Set(["tenant", "tenant_id"]);

export function qs(params: Record<string, string | number | boolean | undefined | null> = {}): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (FORBIDDEN_PARAMS.has(k)) {
      throw new Error(`forbidden request parameter: ${k}`);
    }
    if (v === undefined || v === null || v === "") continue;
    out.set(k, String(v));
  }
  const s = out.toString();
  return s ? `?${s}` : "";
}

export function clampLimit(n: number | undefined): number {
  if (!n || !Number.isFinite(n)) return PAGE_DEFAULT;
  return Math.max(1, Math.min(Math.floor(n), PAGE_CAP));
}

let BASE = "/api/v1";
/** test seam only — production always speaks to the same origin. */
export function setApiBase(base: string): void {
  BASE = base;
}
export function apiBase(): string {
  return BASE;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: "same-origin", ...init });
  if (res.status === 401) throw new ApiError(401, "not authenticated");
  if (res.status === 403) throw new ApiError(403, "admin only");
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => res.statusText));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function get<T>(path: string): Promise<T> {
  return req<T>(path);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** `post_id` may embed a URL with slashes (axios). Always encode. */
export const pid = (postId: string) => encodeURIComponent(postId);

/**
 * Pick the three bracket parameters and NOTHING else.
 *
 * Explicit rather than a spread because react-query hands its queryFn a
 * context object, and `queryFn: api.drift` is a spelling one keystroke away
 * from `queryFn: () => api.drift(r)`. A spread would put `queryKey` and
 * `signal` on the wire; this drops them.
 */
function bk(p: Bracketed | undefined): Record<string, string | null | undefined> {
  return { since: p?.since, until: p?.until, scope_label: p?.scope_label };
}

/* --------------------------------------------------------------- the calls */

/** The two instants and the display label every bracketed call carries. The
 *  server keys its cache and its cursor on the INSTANTS; `scope_label` is
 *  display only and is deliberately outside both. */
export interface Bracketed {
  since?: string | null;
  until?: string | null;
  scope_label?: string;
}

export interface CaseFilters extends Bracketed {
  cursor?: string;
  limit?: number;
  source?: string;
  state?: string;
  verdict?: string;
  only?: "strikes" | "faults";
}

export interface TradeFilters extends Bracketed {
  cursor?: string;
  limit?: number;
  instrument?: string;
  direction?: string;
  venue?: string;
  outcome?: "win" | "loss" | "flat" | "open" | "unknown";
  sort?: "open_ts" | "pnl_usd";
  dir?: "desc" | "asc";
}

export interface DriftRow {
  hop: string;
  /** the bracket the reader chose … */
  p50_cur: number | null;
  n_cur: number;
  /** … and the same length immediately before it. */
  p50_prev: number | null;
  n_prev: number;
  clock: Clock | null;
  certainty: Certainty | null;
  delta_pct: number | null;
}

/** The column headings come from the PAYLOAD. An all-time bracket has no
 *  length to step back by, so the server keeps its documented 24 h vs 7 d
 *  pair and says so here — the client never hardcodes either. */
export interface DriftPayload {
  rows: DriftRow[];
  cur_label: string;
  prev_label: string;
  window?: WindowSpec;
  note: string;
}

export const api = {
  me: () =>
    get<{
      state: "needs_setup" | "needs_login" | "authed";
      username?: string;
      tenant_id?: string;
      admin?: boolean;
      tz?: string;
    }>("/auth/me"),
  login: (username: string, password: string) => post<{ ok: boolean }>("/auth/login", { username, password }),
  logout: () => post<{ ok: boolean }>("/auth/logout"),
  whoami: () => get<{ tenant_id: string; admin: boolean }>("/auth/whoami"),

  /** LIVE. Never cached, never 304 — a cached liveness reading is a lie. */
  state: () => get<StatePayload>("/state"),

  conditions: () =>
    get<{
      conditions: Array<{
        name: string;
        state: string;
        blocks: string | null;
        degraded_telemetry: boolean;
        stale_age_s: number | null;
        read_at: string;
      }>;
      projected: boolean;
    }>("/conditions"),

  drift: (p: Bracketed = {}) => get<DriftPayload>(`/drift${qs(bk(p))}`),

  /** `days` is a DEPRECATED alias kept for one release; since/until win. The
   *  bare-number call shape is still accepted so an un-migrated call site
   *  degrades to the old reading rather than to a 400. */
  spans: (p: (Bracketed & { days?: number }) | number = {}) =>
    typeof p === "number"
      ? get<any>(`/spans${qs({ days: p })}`)
      : get<any>(`/spans${qs({ ...bk(p), days: p.days })}`),

  cases: (f: CaseFilters = {}) => get<Page<CaseRow>>(`/cases${qs({ ...f, limit: clampLimit(f.limit) })}`),

  /** THE per-bracket aggregate. One row per figure, straight from SQL — the
   *  client never derives a summary number from a page it fetched. */
  summary: (p: Bracketed & { source?: string } = {}) =>
    get<SummaryPayload>(`/summary${qs({ ...bk(p), source: p.source })}`),

  case: (postId: string, asOf?: string) => get<any>(`/cases/${pid(postId)}${qs({ as_of: asOf })}`),
  caseRaw: (postId: string) =>
    get<{
      post_id: string;
      refs: Array<{
        band: string;
        journal: string;
        byte_offset: number;
        length: number;
        line_hash: string | null;
        seq: number;
      }>;
    }>(`/cases/raw/${pid(postId)}`),

  /** Today's all-time figures are simply the `all` bracket — one code path,
   *  not two. Every pre-existing field keeps its shape; `window` and `money`
   *  are additive. */
  ledger: (p: Bracketed = {}) =>
    get<{
      realized: Attested;
      sortino: number | null;
      sharpe: null;
      sharpe_suppressed_reason: string;
      max_drawdown_pct: number | null;
      denominators: Denominators;
      n_open: number;
      note: string;
      window?: WindowSpec;
      money?: SummaryMoney;
    }>(`/ledger${qs(bk(p))}`),
  /** The bracket bounds the PAYLOAD, never the CONSTRUCTION: the anchor and
   *  the back-walk always see the whole history, so the curve does not change
   *  shape when the reader changes the range. */
  equity: (p: Bracketed & { days?: number } = {}) =>
    get<EquityPayload>(`/ledger/equity${qs({ ...bk(p), days: p.days })}`),
  trades: (p: TradeFilters = {}) => get<Page<OrderRow>>(`/ledger/trades${qs({ ...p, limit: clampLimit(p.limit) })}`),
  order: (tradeId: string) => get<any>(`/ledger/order/${encodeURIComponent(tradeId)}`),

  /** Brackets the LIVE arm only. A CLAIM carries its own window and is a
   *  research fact about the system, so it must not move when the reader
   *  moves theirs. */
  cells: (p: Bracketed = {}) => get<any>(`/cells${qs(bk(p))}`),
  cell: (source: string, instrument: string, direction: string) =>
    get<any>(`/cells/${encodeURIComponent(source)}/${encodeURIComponent(instrument)}/${encodeURIComponent(direction)}`),

  /** OPEN faults come back whatever the bracket says: a money fault that
   *  opened last month is still money at risk right now. The bracket filters
   *  the resolved and closed sets. */
  faults: (p: Bracketed & { include_closed?: boolean; limit?: number } = {}) =>
    get<any>(`/faults${qs({ ...bk(p), include_closed: p.include_closed, limit: clampLimit(p.limit) })}`),
  fault: (cls: string, anchorId: string) =>
    get<any>(`/faults/${encodeURIComponent(cls)}/${encodeURIComponent(anchorId)}`),

  gates: () =>
    get<{
      gates: GateRow[];
      writable: boolean;
      headline: string;
      /** a pre-migration-003 dashboard.db has no updated_at/updated_by. The
       *  switches still work — the bot reads name+enabled — but flips are not
       *  attributable, and that is stated rather than rendered as "—". */
      audit_columns_absent?: string[];
      degraded?: boolean;
      degraded_note?: string;
    }>("/gates"),
  /** THE one bot-facing write. Admin only; a non-admin gets 403 here. */
  setGate: (name: string, enabled: boolean) => post<any>(`/gates/${encodeURIComponent(name)}`, { enabled }),

  tick: (sinceSeq: number) =>
    get<{
      frames: Array<{ seq: number; at: string; kind: string; payload: string }>;
      last_seq: number;
      quiet_is_normal: boolean;
    }>(`/live/tick${qs({ since_seq: sinceSeq })}`),

  admin: {
    fleet: () => get<any>("/admin/fleet"),
    conditions: () => get<any>("/admin/conditions"),
    mesh: () => get<any>("/admin/mesh"),
    venue: () => get<any>("/admin/venue"),
    firehose: () => get<any>("/admin/firehose"),
    secrets: () => get<any>("/admin/secrets"),
    index: () => get<any>("/admin/index"),
    rebuild: () => post<any>("/admin/index/rebuild"),
    tenants: () => get<any>("/admin/tenants"),
    updater: () => get<any>("/admin/updater/health"),
  },

  /** SSE. Read-only by construction: an EventSource cannot carry a write. */
  stream(onFrame: (kind: string, data: unknown, seq: number) => void): () => void {
    if (typeof EventSource === "undefined") return () => {};
    const es = new EventSource(`${BASE}/live/stream`, { withCredentials: true });
    const handler = (ev: MessageEvent) => {
      try {
        onFrame(ev.type, JSON.parse(ev.data), Number(ev.lastEventId));
      } catch {
        /* a malformed frame is dropped, never guessed at */
      }
    };
    ["case", "amend", "fault", "condition"].forEach((k) => es.addEventListener(k, handler as EventListener));
    return () => es.close();
  },
};
